import { computed, reactive, ref } from 'vue';
import { useBackendAPI } from '@nuxt-shared/composables/useBackendAPI';
import type { AfterSubmitPayload } from '@nuxt-features/form-pers-consultation';
import type {
  ServiceModalContent,
  TariffApiData,
  TariffField,
  TariffItem,
  TariffValue,
} from './types';

const asString = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

const parseNumber = (value: unknown): number =>
  Number.parseFloat(asString(value).replace(',', '.'));

const formatInteger = (value: number): string =>
  String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

const keepFootnoteWithText = (value: string): string =>
  value
    .replace(
      /([^\s<>]+?)(?:\s+|&nbsp;)?(<a\b[^>]*href=["']#tariff_footnote_1["'][^>]*>[\s\S]*?<\/a>)/gi,
      '<span class="rates-tariff-modal__footnote-reference">$1&nbsp;$2</span>',
    )
    .replace(/\s+(<a\s+href=["']#tariff_footnote_)/gi, '&nbsp;$1');

const getFootnoteIds = (footerText: string): Set<string> =>
  new Set(
    Array.from(
      footerText.matchAll(/\bid=["'](tariff_footnote_\d+)["']/gi),
      (match) => match[1],
    ),
  );

const linkBareFootnotes = (value: string, footnoteIds: Set<string>): string =>
  value.replace(
    /<a\b[^>]*>[\s\S]*?<\/a>|<sup\b[^>]*>(\d+)<\/sup>/gi,
    (match, footnoteNumber: string | undefined) => {
      if (!footnoteNumber) return match;

      const footnoteId = `tariff_footnote_${footnoteNumber}`;
      return footnoteIds.has(footnoteId)
        ? `<a href="#${footnoteId}" class="t4">${match}</a>`
        : match;
    },
  );

const removeEmptyParagraphs = (value: string): string =>
  value.replace(/<p\b[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '');

const formatTariffValue = (source: TariffValue): TariffValue => {
  const value = parseNumber(source.VALUE);
  const minValue = parseNumber(source.MIN_VALUE);
  const wage = source.WAGE_TYPE === 'PRC' ? '%' : ` ${source.VALUE_CURR ?? ''}`;
  const formattedMin = Number.isInteger(minValue)
    ? formatInteger(minValue)
    : String(minValue).replace('.', ',');
  const minimum =
    minValue !== 0 && !Number.isNaN(minValue)
      ? [source.MIN_VALUE_TEXT, formattedMin, source.MIN_VALUE_CURR ?? ''].join(' ')
      : '';

  let formattedValue: string;

  if (source.ADD_INFO?.includes('RUSFAR')) {
    formattedValue = source.ADD_INFO.replace('#value#', `${value}${wage}`);
  } else {
    formattedValue = value !== 0 ? `${source.VALUE}${wage}${minimum}` : '—';
    if (source.ADD_INFO) formattedValue += ` ${source.ADD_INFO}`;
  }

  return { ...source, FORMAT_VALUE: formattedValue.trim() };
};

const combineNoteRanges = (fields: TariffField[]): TariffField[] => {
  if (fields.length === 0 || fields.length === 2) return fields;

  let buyRange: TariffField | null = null;
  let sellRange: TariffField | null = null;

  for (const field of fields) {
    if (field.CODE.includes('NoteBuyGroup')) {
      if (!buyRange) {
        buyRange = field;
      } else if (buyRange.VALUES[0] && field.VALUES[0]) {
        buyRange.VALUES[0].VALUE += `-${field.VALUES[0].VALUE}`;
        buyRange.VALUES[0] = formatTariffValue(buyRange.VALUES[0]);
      }
    }

    if (field.CODE.includes('NoteSell')) {
      if (!sellRange) {
        sellRange = field;
      } else if (sellRange.VALUES[0] && field.VALUES[0]) {
        sellRange.VALUES[0].VALUE += `-${field.VALUES[0].VALUE}`;
        sellRange.VALUES[0] = formatTariffValue(sellRange.VALUES[0]);
      }
    }
  }

  return buyRange || sellRange
    ? [buyRange, sellRange].filter((field): field is TariffField => Boolean(field))
    : fields;
};

const normalizeNode = (
  source: unknown,
  footnoteIds: Set<string>,
  shouldLinkFootnotes = true,
): unknown => {
  if (Array.isArray(source)) {
    return source.map((item) => normalizeNode(item, footnoteIds, shouldLinkFootnotes));
  }
  if (typeof source === 'string') {
    const value = shouldLinkFootnotes ? linkBareFootnotes(source, footnoteIds) : source;
    return keepFootnoteWithText(value);
  }
  if (!source || typeof source !== 'object') return source;

  const result = Object.fromEntries(
    Object.entries(source).map(([key, value]) => [
      key,
      normalizeNode(value, footnoteIds, shouldLinkFootnotes && key !== 'FOOTER_TEXT'),
    ]),
  ) as Record<string, unknown>;

  if (Array.isArray(result.VALUES)) {
    result.VALUES = (result.VALUES as TariffValue[]).map(formatTariffValue);
  }

  if (Array.isArray(result.FIELDS)) {
    result.FIELDS = combineNoteRanges(result.FIELDS as TariffField[]);
  }

  return result;
};

const findTransactionFee = (tariff: TariffItem): string => {
  for (const section of tariff.SECTIONS ?? []) {
    for (const subsection of section.SUBSECTIONS ?? []) {
      const field = subsection.FIELDS?.find(
        (candidate) => candidate.CODE === 'SecurityTransactionsQUIK',
      );
      const value = field?.VALUES?.[0];
      if (value) return value.FORMAT_VALUE || formatTariffValue(value).FORMAT_VALUE || '';
    }
  }

  return '';
};

const normalizeTariff = (source: TariffItem): TariffItem => {
  const footnoteIds = getFootnoteIds(asString(source.FOOTER_TEXT));
  const tariff = normalizeNode(source, footnoteIds) as TariffItem;
  const startSum = asString(tariff.ASSETS_DESC).replace(/\D/g, '');
  const signs = startSum
    ? (asString(tariff.ASSETS_DESC)
        .replace(/&nbsp;/g, '')
        .split(startSum)
        .at(-1)
        ?.trim()
        .split(/\s+/) ?? [])
    : [];

  tariff.START_SUM = startSum;
  tariff.DESC = startSum
    ? 'Тариф доступен <br> для&nbsp;квалифицированных инвесторов'
    : asString(tariff.ASSETS_DESC);
  tariff.SIGNS = signs;
  tariff.TRANSACTION_FEE = findTransactionFee(tariff);

  return tariff;
};

export async function useRatesModel() {
  const response = await useBackendAPI('tariff/', {
    key: 'rates-tariffs',
    method: 'GET',
  });

  const data = computed<TariffApiData>(() =>
    response?.data.value && typeof response.data.value === 'object'
      ? (response.data.value as TariffApiData)
      : {},
  );
  const title = computed(() => asString(data.value.TITLE));
  const description = computed(() =>
    removeEmptyParagraphs(asString(data.value.DESCRIPTION)),
  );
  const tariffs = computed(() =>
    Array.isArray(data.value.ITEMS) ? data.value.ITEMS.map(normalizeTariff) : [],
  );

  const activeTariffCode = ref('');
  const isTariffModalOpen = ref(false);
  const scheduleHeader = ref<HTMLElement | null>(null);
  const activeTariff = computed(
    () =>
      tariffs.value.find((tariff) => tariff.CODE === activeTariffCode.value) ??
      tariffs.value[0] ??
      null,
  );

  const openTariff = (code: string) => {
    activeTariffCode.value = code;
    isTariffModalOpen.value = true;
  };
  const selectTariff = (code: string) => {
    activeTariffCode.value = code;
  };
  const closeTariff = () => {
    isTariffModalOpen.value = false;
  };

  const syncScheduleHeader = (event: Event) => {
    const target = event.currentTarget as HTMLElement | null;
    if (!target || !scheduleHeader.value) return;
    scheduleHeader.value.style.transform = `translateX(-${target.scrollLeft}px)`;
  };

  const serviceModal = reactive<ServiceModalContent>({ title: '', description: '' });
  const isServiceModalOpen = ref(false);
  const handleFormSubmit = ({
    title: modalTitle,
    description: modalDescription,
  }: AfterSubmitPayload) => {
    serviceModal.title = modalTitle;
    serviceModal.description = modalDescription;
    isServiceModalOpen.value = true;
  };
  const closeServiceModal = () => {
    isServiceModalOpen.value = false;
  };

  return {
    title,
    description,
    tariffs,
    activeTariff,
    activeTariffCode,
    isTariffModalOpen,
    scheduleHeader,
    serviceModal,
    isServiceModalOpen,
    openTariff,
    selectTariff,
    closeTariff,
    syncScheduleHeader,
    handleFormSubmit,
    closeServiceModal,
  };
}
